"use client";

import React, {useEffect} from "react";
import Image from "next/image";
import {Locale} from "~/i18n-config";
import {usePathname, useSearchParams} from "next/navigation";
import {useClientAuth} from "~/components/providers/client/AuthProvider";
import {CloseContextProvider} from "~/components/providers/client/CloseContextProvider";
import {AnimatePresence, motion} from "framer-motion";
import {Popover} from "@headlessui/react";
import clsx from "clsx";
import {LangLink} from "~/components/locales/LangLink";

export function HeaderBar(
    { lang, homeUrl, main, mainMobile, account, accountMobile, className } :
        {
            lang: Locale,
            homeUrl: string,
            main: React.ReactNode,
            mainMobile: React.ReactNode,
            account: React.ReactNode,
            accountMobile: React.ReactNode,
            className?: string
        }
) {

    const pathname = usePathname();
    const searchParams = useSearchParams();

    const showNavigation = !pathname.includes("/signup/");

    const { user } = useClientAuth();

    const companyParam = searchParams.get("company");
    const assetParam = searchParams.get("asset");

    let homeUrlWithSearchParams = homeUrl;

    if (companyParam || assetParam) {
        const params = new URLSearchParams();
        if (companyParam) params.append("company", companyParam);
        if (assetParam) params.append("asset", assetParam);
        homeUrlWithSearchParams = `${homeUrl}?${params.toString()}`;
    }

    return (
        <header className={clsx("px-2", className)}>
            <nav>
                <div className="mx-auto max-w-7xl flex justify-between px-4 py-6">
                    <div className="flex items-center gap-20 h-30">
                        <div className="grow-0">
                            <LangLink className="relative block w-[164px] h-[40px]" lang={lang} href={homeUrlWithSearchParams}>
                                <Image priority={true} fill={true} alt="DocID" src="/images/logo-docid-white.svg" />
                            </LangLink>
                        </div>
                        {showNavigation && main}
                    </div>
                    <div className="flex items-center">
                        <Popover className="lg:hidden">
                            {({ open, close }) => {

                                useEffect(() => {
                                    close();
                                }, [pathname]);

                                return (
                                    <CloseContextProvider closeAction={() => close()}>
                                        <Popover.Button className="absolute !z-[1001] top-4 right-4 w-[50px] h-[50px] inline-flex outline-none" aria-label="Open navigation">
                                            <div className="relative">
                                                <div className="relative flex overflow-hidden items-center justify-center rounded-full w-[50px] h-[50px] transform transition-all bg-zinc-800 ring-0 hover:ring-4 ring-white/60 duration-200 shadow-md">
                                                    <div className="flex flex-col justify-between w-[20px] h-[20px] transform transition-all duration-300 origin-center overflow-hidden">
                                                        <div className={clsx("bg-white h-[2px] w-7 transform transition-all duration-300 origin-left", open ? "translate-x-10" : "")}></div>
                                                        <div className={clsx("bg-white h-[2px] w-7 rounded transform transition-all duration-300 delay-75", open ? "translate-x-10" : "")}></div>
                                                        <div className={clsx("bg-white h-[2px] w-7 transform transition-all duration-300 origin-left delay-150", open ? "translate-x-10" : "")}></div>
                                                        <div className={clsx("absolute items-center justify-between transform transition-all duration-500 top-2.5 flex w-0 -translate-x-10", open ? "translate-x-0 w-12" : "")}>
                                                            <div className={clsx("absolute bg-white h-[2px] w-5 transform transition-all duration-500 rotate-0 delay-300", open ? "rotate-45" : "")}></div>
                                                            <div className={clsx("absolute bg-white h-[2px] w-5 transform transition-all duration-500 -rotate-0 delay-300", open ? "-rotate-45" : "")}></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Popover.Button>
                                        <AnimatePresence initial={false}>
                                            {open && (
                                                <>
                                                    <Popover.Overlay
                                                        static
                                                        as={motion.div}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        className="fixed inset-0 !z-[998] bg-white/20 backdrop-blur"
                                                    />
                                                    <Popover.Panel
                                                        static
                                                        as={motion.div}
                                                        initial={{ opacity: 0, y: -20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{
                                                            opacity: 0,
                                                            y: -20,
                                                            transition: { duration: 0.2 },
                                                        }}
                                                        className="absolute inset-x-0 top-0 !z-[999] origin-top pt-5 rounded-b-2xl bg-white shadow-2xl shadow-zinc-900/20">
                                                        <LangLink href={(user) ? "/dashboard" : "/"} aria-label="DocID" className="block mx-6" lang={lang}>
                                                            <Image src="/images/logo-docid.svg" width={160} height={34} alt="DocID" />
                                                        </LangLink>
                                                        {mainMobile}
                                                        {accountMobile}
                                                    </Popover.Panel>
                                                </>
                                            )}
                                        </AnimatePresence>
                                    </CloseContextProvider>
                                )
                            }}
                        </Popover>
                        {account}
                    </div>
                </div>
            </nav>
        </header>
    )
}
